import { useState, useEffect, useRef } from "react";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
	type DragStartEvent,
	DragOverlay,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "@/components/SortableItem";
import { TodoItem } from "@/components/TodoItem";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, ListTodo, Moon, Sun } from "lucide-react";
import "./index.css";

export interface Todo {
	id: string;
	text: string;
	completed: boolean;
}

export function App() {
	const [todos, setTodos] = useState<Todo[]>([]);
	const [newTodo, setNewTodo] = useState("");
	const [activeId, setActiveId] = useState<string | null>(null);
	const [filter, setFilter] = useState<"all" | "active" | "completed">("all");
	const [dark, setDark] = useState(() => {
		if (typeof window !== "undefined") {
			const stored = localStorage.getItem("theme");
			if (stored) return stored === "dark";
			return window.matchMedia("(prefers-color-scheme: dark)").matches;
		}
		return false;
	});
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		document.documentElement.classList.toggle("dark", dark);
		localStorage.setItem("theme", dark ? "dark" : "light");
	}, [dark]);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { distance: 5 },
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	useEffect(() => {
		fetch("/api/todos")
			.then((res) => res.json())
			.then((data: Todo[]) => setTodos(data))
			.catch(() => {});
	}, []);

	const saveTodos = (updated: Todo[]) => {
		setTodos(updated);
		fetch("/api/todos", {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(updated),
		}).catch(() => {});
	};

	const addTodo = () => {
		const text = newTodo.trim();
		if (!text) return;
		const todo: Todo = {
			id: crypto.randomUUID(),
			text,
			completed: false,
		};
		saveTodos([...todos, todo]);
		setNewTodo("");
		inputRef.current?.focus();
	};

	const toggleTodo = (id: string) => {
		saveTodos(
			todos.map((t) => (t.id === id ? { ...t, completed: !t.completed } : t))
		);
	};

	const deleteTodo = (id: string) => {
		saveTodos(todos.filter((t) => t.id !== id));
	};

	const editTodo = (id: string, text: string) => {
		saveTodos(todos.map((t) => (t.id === id ? { ...t, text } : t)));
	};

	const handleDragStart = (event: DragStartEvent) => {
		setActiveId(event.active.id as string);
	};

	const handleDragEnd = (event: DragEndEvent) => {
		setActiveId(null);
		const { active, over } = event;
		if (over && active.id !== over.id) {
			const oldIndex = todos.findIndex((t) => t.id === active.id);
			const newIndex = todos.findIndex((t) => t.id === over.id);
			saveTodos(arrayMove(todos, oldIndex, newIndex));
		}
	};

	const filteredTodos = todos.filter((t) => {
		if (filter === "active") return !t.completed;
		if (filter === "completed") return t.completed;
		return true;
	});

	const activeTodo = activeId ? todos.find((t) => t.id === activeId) : null;
	const remaining = todos.filter((t) => !t.completed).length;
	const completedCount = todos.filter((t) => t.completed).length;

	return (
		<div className="w-full max-w-lg mx-auto p-4 sm:p-8">
			<Card className="shadow-lg border-border/50">
				<CardHeader className="pb-4">
					<div className="flex items-center justify-between">
						<CardTitle className="flex items-center gap-2 text-2xl">
							<ListTodo className="h-6 w-6" />
							Todos
						</CardTitle>
						<Button
							variant="ghost"
							size="icon"
							onClick={() => setDark((d) => !d)}
							aria-label="Toggle dark mode"
							className="h-8 w-8"
						>
							{dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
						</Button>
					</div>
					<p className="text-sm text-muted-foreground">
						{remaining} remaining{completedCount > 0 && ` · ${completedCount} done`}
					</p>
				</CardHeader>
				<CardContent className="space-y-4">
					<form
						onSubmit={(e) => {
							e.preventDefault();
							addTodo();
						}}
						className="flex gap-2"
					>
						<Input
							ref={inputRef}
							value={newTodo}
							onChange={(e) => setNewTodo(e.target.value)}
							placeholder="What needs to be done?"
							className="flex-1"
							autoFocus
						/>
						<Button type="submit" size="icon" disabled={!newTodo.trim()}>
							<Plus className="h-4 w-4" />
						</Button>
					</form>

					{todos.length > 0 && (
						<div className="flex gap-1 text-xs">
							{(["all", "active", "completed"] as const).map((f) => (
								<button
									key={f}
									onClick={() => setFilter(f)}
									className={`px-3 py-1.5 rounded-md capitalize transition-colors ${
										filter === f
											? "bg-primary text-primary-foreground"
											: "text-muted-foreground hover:bg-muted"
									}`}
								>
									{f}
								</button>
							))}
							{completedCount > 0 && (
								<button
									onClick={() =>
										saveTodos(todos.filter((t) => !t.completed))
									}
									className="ml-auto px-3 py-1.5 rounded-md text-destructive hover:bg-destructive/10 transition-colors"
								>
									Clear done
								</button>
							)}
						</div>
					)}

					{filteredTodos.length === 0 && todos.length === 0 && (
						<p className="text-center text-muted-foreground py-8 text-sm">
							Add your first todo to get started
						</p>
					)}

					{filteredTodos.length === 0 && todos.length > 0 && (
						<p className="text-center text-muted-foreground py-8 text-sm">
							No {filter} todos
						</p>
					)}

					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragStart={handleDragStart}
						onDragEnd={handleDragEnd}
					>
						<SortableContext
							items={filteredTodos.map((t) => t.id)}
							strategy={verticalListSortingStrategy}
						>
							<div className="space-y-1.5">
								{filteredTodos.map((todo) => (
									<SortableItem
										key={todo.id}
										todo={todo}
										onToggle={toggleTodo}
										onDelete={deleteTodo}
										onEdit={editTodo}
									/>
								))}
							</div>
						</SortableContext>
						<DragOverlay>
							{activeTodo ? (
								<TodoItem
									todo={activeTodo}
									onToggle={toggleTodo}
									onDelete={deleteTodo}
									onEdit={editTodo}
									isDragOverlay
								/>
							) : null}
						</DragOverlay>
					</DndContext>
				</CardContent>
			</Card>
		</div>
	);
}

export default App;
